import React, { Component } from 'react';
import { FormattedHTMLMessage, injectIntl, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import { ACTOR, FLOW_TYPE, CREDIT_TYPE, REDIRECT_SUFFIX } from '../../../framework/App/AppConstants';
import IncentiveInfo from './IncentiveInfo';
import RedirectButton from '../../../shared-components/RedirectButton/RedirectButton';
import Col from '../../../shared-components/VXGrid/Col';
import Row from '../../../shared-components/VXGrid/Row';
import NotificationBanner, { NOTIFICATION_TYPE } from '../../../shared-components/NotificationBanner/NotificationBanner';
import { APPLY_TYPE, APPLICATION_STATUS } from '../../USConstants';

const LOCALE_PREFIX = 'US/pages/DecisionPage/DecisionApproved';
const APPROVED_MESSAGE = 'approved-message';
const LINKED_APPROVAL_MESSAGE = 'approved-link-message';

export class DecisionApproved extends Component {
    static propTypes = {
        intl: intlShape.isRequired,
        flow: PropTypes.oneOf(Object.values(FLOW_TYPE)),
        actor: PropTypes.oneOf(Object.values(ACTOR)),
        financingDetails: PropTypes.object,
        creditType: PropTypes.string.isRequired,
        creditLine: PropTypes.object,
        applyType: PropTypes.string.isRequired,
        applicationStatus: PropTypes.string,
        incentivesDetails: PropTypes.object,
        creditIncentiveOutcome: PropTypes.string,
        isBAWithoutPurchase: PropTypes.bool,
        isLinkApplicationFlow: PropTypes.bool,
        userFirstName: PropTypes.string
    };

    constructor() {
        super();
        this.extendedApprovalMessageKey = this.extendedApprovalMessageKey.bind(this);
        this.approvedButtonLabel = this.approvedButtonLabel.bind(this);
    }

    extendedApprovalMessageKey() {
        const { applicationStatus, creditType, financingDetails, flow, isLinkApplicationFlow } = this.props;

        if (applicationStatus === APPLICATION_STATUS.PENDING_ACCOUNT_CREATION ||
            applicationStatus === APPLICATION_STATUS.PENDING_MANUAL_REMEDIATION ||
            isLinkApplicationFlow) {
            return undefined;
        } else if (creditType === CREDIT_TYPE.INSTALLMENTS && (financingDetails && financingDetails.monthly_payment)) {
            return 'installment-approved-message';
        } else if (flow === FLOW_TYPE.DIRECT_APP) {
            return 'direct-apply-approved-message';
        }
    }

    pendingAccountCreationMessageKey() {
        const { applicationStatus, flow } = this.props;

        if ((flow === FLOW_TYPE.HERMES) || (flow === FLOW_TYPE.PROX)) {
            return 'pending-account-creation-checkout-message';
        } else {
            return 'pending-account-creation-non-checkout-message';
        }
    }

    pendingManualRemediationMessageKey() {
        const { applicationStatus, flow } = this.props;

        if ((flow === FLOW_TYPE.HERMES) || (flow === FLOW_TYPE.PROX)) {
            return 'pending-manual-remediation-checkout-message';
        } else {
            return 'pending-manual-remediation-non-checkout-message';
        }
    }

    approvedButtonLabel() {
        let result = 'approved-button-label';
        const { intl, flow, actor } = this.props;

        if (flow === FLOW_TYPE.DIRECT_APP && actor === ACTOR.MERCHANT) {
            result = 'approved-button-label-for-da-merchant';
        } else if (flow === FLOW_TYPE.PROX || flow === FLOW_TYPE.HERMES) {
            result = 'approved-button-label-for-transaction';
        }

        return intl.formatHTMLMessage({ id: `${LOCALE_PREFIX}.${result}` });
    }

    render() {
        const { applicationStatus, applyType, creditLine, financingDetails,
            isLinkApplicationFlow, userFirstName, flow } = this.props;
        const creditLineLimitMessage = (
            <FormattedHTMLMessage
                id={`${LOCALE_PREFIX}.${APPROVED_MESSAGE}`}
                values={{ credit_line_format: (creditLine && creditLine.format) || '' }}
            />
        );
        const creditLineExtendedMessage = (this.extendedApprovalMessageKey() &&
            <FormattedHTMLMessage
                id={`${LOCALE_PREFIX}.${this.extendedApprovalMessageKey()}`}
                values={{
                    credit_line_format: (creditLine && creditLine.format) || '',
                    monthly_payment: (financingDetails && financingDetails.monthly_payment) || ''
                }}
            />
        );
        const pendingAccountCreationMessage = (this.pendingAccountCreationMessageKey() &&
            <FormattedHTMLMessage
                id={`${LOCALE_PREFIX}.${this.pendingAccountCreationMessageKey()}`}
            />
        );
        const pendingManualRemediationMessage = (this.pendingManualRemediationMessageKey() &&
            <FormattedHTMLMessage
                id={`${LOCALE_PREFIX}.${this.pendingManualRemediationMessageKey()}`}
            />
        );

        const LinkedCreditLineMessage = (
            <FormattedHTMLMessage
                id={`${LOCALE_PREFIX}.${LINKED_APPROVAL_MESSAGE}`}
            />
        );

        const isFastApply = applyType === APPLY_TYPE.FAST;
        let decisionMessage;
        let decisionHeader;

        if (isLinkApplicationFlow) {
            decisionMessage = (
                <div id="decisionMessaging" className="decisionMessaging-link">
                    <div id="LinkedCreditLineMessage">
                        {LinkedCreditLineMessage}
                    </div>
                </div>
            );
            decisionHeader = (
                <h2 className="alpha">
                    <FormattedHTMLMessage
                        id={`${LOCALE_PREFIX}.approved-link-header`}
                        values={{ userFirstName: userFirstName || '' }}
                    />
                </h2>
            );
        } else {

            decisionHeader = (
                <h2 className="alpha">
                    <FormattedHTMLMessage id={`${LOCALE_PREFIX}.approved-header`} />
                </h2>
            );

            if (applicationStatus === APPLICATION_STATUS.PENDING_ACCOUNT_CREATION) {
                decisionMessage = (
                    <div id="decisionMessaging" className="decisionMessaging">
                        <div id="creditLineLimitMessage">
                            {creditLineLimitMessage}
                        </div>
                        <div id="notifications">
                            <NotificationBanner message={pendingAccountCreationMessage} type={NOTIFICATION_TYPE.WARNING_OUTLINE} />
                        </div>
                    </div>
                );
            } else if (applicationStatus === APPLICATION_STATUS.PENDING_MANUAL_REMEDIATION) {
                decisionMessage = (
                    <div id="decisionMessaging" className="decisionMessaging">
                        <div id="creditLineLimitMessage">
                            {creditLineLimitMessage}
                        </div>
                        <div id="notifications">
                            <NotificationBanner message={pendingManualRemediationMessage} type={NOTIFICATION_TYPE.WARNING_OUTLINE} />
                        </div>
                    </div>
                );
            } else if (flow === FLOW_TYPE.DIRECT_APP) {
                decisionHeader = (
                    <React.Fragment>
                        <div className="decisionHeaderApprovedMessage">
                            <FormattedHTMLMessage id={`${LOCALE_PREFIX}.approved-message-da`} />
                        </div>
                        <div className="decisionHeaderCreditLine">
                            {creditLine && creditLine.format}
                        </div>
                        <div className="decisionApprovedMessage">
                            <FormattedHTMLMessage id={`${LOCALE_PREFIX}.approved-header`} />
                        </div>
                    </React.Fragment>
                );
                decisionMessage = (
                    <div id="decisionMessaging" className="decisionMessaging">
                        <hr className="vx_hr decisionMessagingDivider" />
                        <div className="creditLineExtendedMessage">
                            {creditLineExtendedMessage}
                        </div>
                    </div>
                );
            } else {
                decisionMessage = (
                    <div id="decisionMessaging" className="decisionMessaging">
                        <div id="creditLineLimitMessage">
                            {creditLineLimitMessage}
                        </div>
                        <div id="creditLineExtendedMessage">
                            {creditLineExtendedMessage}
                        </div>
                    </div>
                );
            }
        }
    
        return (
            <div>
                <section id="decision" className="decision-approved">
                    <Row fluid={true} className="container" id="approved">
                        <Col xs={12}>
                            <div className="decision-content text-center">

                                <div className="approved-check successImage" />

                                {decisionHeader}

                                {decisionMessage}

                                <div className="decision-incentivesDefaulting">
                                    <IncentiveInfo {...this.props} />
                                </div>
                            </div>
                            <Row>
                                <Col sm={8} smOffset={2}>
                                    <div className="buttons">
                                        <RedirectButton
                                            ctaLabel={this.approvedButtonLabel()}
                                            className="decisionReturnFlowAction"
                                            buttonId="approvedContinue"
                                            actionUri={REDIRECT_SUFFIX.RETURN_REDIRECT}
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </section>

            </div>
        );
    }
}

export default injectIntl(DecisionApproved);
